import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TelemetryService } from "app/core/services/telemetry.service";
import { ProductEventService } from "app/core/services/product-event.service";
import { environment } from "environments/environment";
import { catchError, defer, filter, merge, Observable, of, shareReplay, startWith, Subject, switchMap, tap, throwError } from "rxjs";
import { Account, CreateAccountRequest } from ".";
import { Subscription } from "./subscription.model";

// https://blog.angular-university.io/rxjs-higher-order-mapping/

@Injectable({ providedIn: 'root' })
export class AccountDataSource {
    private readonly accountUpdates$ = new Subject<Account | null>();
    private readonly subscriptionRefresh$ = new Subject<void>();
    baseUrl: string;
    account$: Observable<Account | null>;

    constructor(
        private http: HttpClient,
        private telemetryService: TelemetryService,
        private productEventService: ProductEventService
    ) {
        this.baseUrl = `${environment.apiUrl}/account`;
        this.account$ = merge(
            defer(() => this.getAccountIfExists()),
            this.accountUpdates$
        ).pipe(shareReplay(1));
    }

    getAccount(): Observable<Account> {
        return this.http.get<Account>(this.baseUrl).pipe(
            tap(() => this.productEventService.recordSession())
        );
    }

    getAccountIfExists(): Observable<Account | null> {
        return this.getAccount().pipe(
            catchError((error: { status?: number }) => {
                if (error.status === 404) {
                    return of(null);
                }

                return throwError(() => error);
            })
        );
    }

    createAccount(request: CreateAccountRequest): Observable<Account> {
        const requestBody = {
            ...request,
            attribution: this.telemetryService.getAdsAttributionPayload()
        };

        return this.http.post<Account>(this.baseUrl, requestBody).pipe(
            tap((account) => {
                this.productEventService.recordSession();
                this.accountUpdates$.next(account);

                if (account.newUser !== true) {
                    return;
                }

                this.telemetryService.trackUiConversionApiRequest({
                    conversionType: 'signup',
                    requestMethod: 'POST',
                    requestUrl: this.baseUrl,
                    requestBody,
                    userId: account.id
                });

                if (!requestBody.attribution) {
                    this.telemetryService.trackUiSignupWithoutAdAttribution({
                        requestMethod: 'POST',
                        requestUrl: this.baseUrl,
                        requestBody,
                        userId: account.id
                    });
                }
            })
        );
    }

    getSubscription(): Observable<Subscription> {
        return this.account$.pipe(
            filter(account => !!account?.id),
            switchMap(() => this.subscriptionRefresh$.pipe(
                startWith(undefined),
                switchMap(() => this.http.get<Subscription>(`${this.baseUrl}/subscription`))
            ))
        );
    }

    updateMarketingPreferences(suppressed: boolean): Observable<Account> {
        return this.http.put<Account>(`${this.baseUrl}/marketing-preferences`, { suppressed }).pipe(
            tap(account => this.accountUpdates$.next(account))
        );
    }

    refreshSubscription() {
        this.subscriptionRefresh$.next();
    }
}
